/**
 * SQL 语句拆分（前端）
 * <p>把一段可能包含多条语句的 SQL 文本，按「顶层分号」拆成语句数组。</p>
 * <p>关键：不能简单地 `split(';')`，分号可能出现在字符串、标识符、注释、美元引用里。
 * 本函数逐字符扫描并跟踪状态，只在「非注释、非引号、非美元引用」的顶层遇到分号才切分。</p>
 *
 * 处理的语法：
 * - 单引号字符串（`''` 转义；可选反斜杠转义，MySQL 需要）
 * - 双引号标识符、反引号标识符（MySQL）
 * - 行注释 `-- ...`、块注释 `/* ... *\/`
 * - PostgreSQL 美元引用 `$$...$$` / `$tag$...$tag$`
 */

/**
 * 判断片段去掉注释后是否还有实际内容（纯注释/空白视为空语句）。
 */
function hasContent(sql) {
    const stripped = sql
        .replace(/\/\*[\s\S]*?\*\//g, ' ')
        .replace(/--[^\n]*/g, ' ');
    return stripped.trim().length > 0;
}

/**
 * 拆分 SQL。
 * @param {string} text SQL 原文
 * @param {object} [opts]
 * @param {boolean} [opts.backslashEscapes=false] 单引号内是否把反斜杠当转义（MySQL=true，PostgreSQL=false）
 * @returns {string[]} 语句数组（已 trim，已去除纯注释/空白片段）
 */
export function splitSql(text, opts = {}) {
    const backslashEscapes = !!opts.backslashEscapes;
    const out = [];
    if (!text) return out;

    const n = text.length;
    let buf = '';
    let i = 0;

    const push = () => {
        const s = buf.trim();
        if (s && hasContent(s)) out.push(s);
        buf = '';
    };

    while (i < n) {
        const ch = text[i];
        const next = text[i + 1];

        // 行注释：-- 直到行尾（换行不算内容，直接进入缓冲）
        if (ch === '-' && next === '-') {
            buf += '--';
            i += 2;
            while (i < n && text[i] !== '\n') {
                buf += text[i];
                i++;
            }
            continue;
        }

        // 块注释：/* ... */
        if (ch === '/' && next === '*') {
            buf += '/*';
            i += 2;
            while (i < n && !(text[i] === '*' && text[i + 1] === '/')) {
                buf += text[i];
                i++;
            }
            if (i < n) {
                buf += '*/';
                i += 2;
            }
            continue;
        }

        // 单引号字符串
        if (ch === "'") {
            buf += ch;
            i++;
            while (i < n) {
                if (text[i] === "'" && text[i + 1] === "'") {
                    buf += "''";
                    i += 2;
                    continue;
                }
                if (backslashEscapes && text[i] === '\\' && i + 1 < n) {
                    buf += text[i] + text[i + 1];
                    i += 2;
                    continue;
                }
                buf += text[i];
                if (text[i] === "'") {
                    i++;
                    break;
                }
                i++;
            }
            continue;
        }

        // 双引号 / 反引号标识符
        if (ch === '"' || ch === '`') {
            const q = ch;
            buf += ch;
            i++;
            while (i < n) {
                if (text[i] === q && text[i + 1] === q) {
                    buf += q + q;
                    i += 2;
                    continue;
                }
                buf += text[i];
                if (text[i] === q) {
                    i++;
                    break;
                }
                i++;
            }
            continue;
        }

        // PostgreSQL 美元引用：$$...$$ / $tag$...$tag$
        if (ch === '$') {
            const m = /^\$[A-Za-z_0-9]*\$/.exec(text.slice(i));
            if (m) {
                const tag = m[0];
                buf += tag;
                i += tag.length;
                const end = text.indexOf(tag, i);
                if (end === -1) {
                    buf += text.slice(i);
                    i = n;
                } else {
                    buf += text.slice(i, end + tag.length);
                    i = end + tag.length;
                }
                continue;
            }
        }

        // 顶层分号：切分
        if (ch === ';') {
            push();
            i++;
            continue;
        }

        buf += ch;
        i++;
    }

    push();
    return out;
}
